import { Paper, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { ReactElement, useMemo, useState } from 'react';
import type { MerchantsOfferSummariesResource } from '../../../../../Entities/MerchantsOfferSummary';
import HeaderCell from './HeaderCell';
import MerchantRows from './MerchantRows';
import { MetricHoverContext, MetricSortContext } from './context';
import { buildTotals, type MetricKey, type SortState } from './utils';

interface MerchantsOfferSummaryTableProps {
  data: MerchantsOfferSummariesResource[];
}

export default function MerchantsOfferSummaryTable({
  data,
}: MerchantsOfferSummaryTableProps): ReactElement {
  const [hoveredMetricKey, setHoveredMetricKey] = useState<MetricKey | null>(null);
  const [sortState, setSortState] = useState<SortState | null>(null);
  const totals = useMemo(() => buildTotals(data), [data]);
  const sortedData = useMemo(() => {
    if (!sortState) {
      return data;
    }

    const direction = sortState.order === 'asc' ? 1 : -1;

    return data.toSorted((left, right) => {
      const metricDifference =
        (left.attributes?.[sortState.metricKey] ?? 0) -
        (right.attributes?.[sortState.metricKey] ?? 0);
      const merchantDifference =
        Number(left.relationships?.merchant?.data?.id ?? 0) -
        Number(right.relationships?.merchant?.data?.id ?? 0);

      return metricDifference * direction || merchantDifference;
    });
  }, [data, sortState]);
  const records = sortedData.length ? [...sortedData, totals] : sortedData;

  const handleHeaderClick = (metricKey: MetricKey): void => {
    setSortState((current) => ({
      metricKey,
      order: current?.metricKey === metricKey && current.order === 'desc' ? 'asc' : 'desc',
    }));
  };

  return (
    <MetricHoverContext.Provider value={{ hoveredMetricKey, setHoveredMetricKey }}>
      <MetricSortContext.Provider value={{ sortState }}>
        <TableContainer
          component={Paper}
          elevation={1}
          sx={{
            overflowX: 'auto',
            bgcolor: 'background.paper',
          }}
        >
          <Table
            size="small"
            sx={{
              bgcolor: 'background.paper',
              '& .MuiTableCell-root': {
                px: 1,
                py: 0.75,
                textAlign: 'center',
                borderBottom: 1,
                borderColor: 'divider',
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                borderBottomWidth: 1,
              },
              '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: 0,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <HeaderCell rowSpan={4}>Merchant</HeaderCell>
                <HeaderCell
                  rowSpan={4}
                  metricKey="total_offers"
                  highlightOnHover={false}
                  onMetricClick={handleHeaderClick}
                >
                  Offers
                </HeaderCell>
                <HeaderCell
                  rowSpan={4}
                  metricKey="categorized_offers"
                  highlightOnHover={false}
                  onMetricClick={handleHeaderClick}
                >
                  Categorized
                </HeaderCell>
                <HeaderCell
                  rowSpan={4}
                  metricKey="not_categorized_offers"
                  highlightOnHover={false}
                  onMetricClick={handleHeaderClick}
                >
                  Not Categorized
                </HeaderCell>
                <HeaderCell
                  rowSpan={4}
                  metricKey="comparable_offers"
                  highlightOnHover={false}
                  onMetricClick={handleHeaderClick}
                >
                  Comparable
                </HeaderCell>
                <HeaderCell
                  rowSpan={4}
                  metricKey="incomparable_offers"
                  highlightOnHover={false}
                  onMetricClick={handleHeaderClick}
                >
                  Incomparable
                </HeaderCell>
                <HeaderCell
                  rowSpan={4}
                  metricKey="combined_offers"
                  highlightOnHover={false}
                  onMetricClick={handleHeaderClick}
                >
                  Combined
                </HeaderCell>
                <HeaderCell
                  rowSpan={4}
                  metricKey="not_combined_offers"
                  highlightOnHover={false}
                  onMetricClick={handleHeaderClick}
                >
                  Not Combined
                </HeaderCell>
                <HeaderCell
                  colSpan={4}
                  metricKey="approved_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Approved
                </HeaderCell>
                <HeaderCell
                  colSpan={4}
                  metricKey="disapproved_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Disapproved
                </HeaderCell>
              </TableRow>
              <TableRow>
                <HeaderCell
                  colSpan={3}
                  metricKey="approved_categorized_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Categorized
                </HeaderCell>
                <HeaderCell
                  metricKey="approved_not_categorized_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Not Categorized
                </HeaderCell>
                <HeaderCell
                  colSpan={3}
                  metricKey="disapproved_categorized_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Categorized
                </HeaderCell>
                <HeaderCell
                  metricKey="disapproved_not_categorized_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Not Categorized
                </HeaderCell>
              </TableRow>
              <TableRow>
                <HeaderCell
                  colSpan={2}
                  metricKey="approved_categorized_comparable_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Comparable
                </HeaderCell>
                <HeaderCell
                  metricKey="approved_categorized_incomparable_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Incomparable
                </HeaderCell>
                <HeaderCell />
                <HeaderCell
                  colSpan={2}
                  metricKey="disapproved_categorized_comparable_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Comparable
                </HeaderCell>
                <HeaderCell
                  metricKey="disapproved_categorized_incomparable_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Incomparable
                </HeaderCell>
                <HeaderCell />
              </TableRow>
              <TableRow>
                <HeaderCell
                  metricKey="approved_categorized_comparable_combined_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Combined
                </HeaderCell>
                <HeaderCell
                  metricKey="approved_categorized_comparable_not_combined_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Not Combined
                </HeaderCell>
                <HeaderCell />
                <HeaderCell />
                <HeaderCell
                  metricKey="disapproved_categorized_comparable_combined_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Combined
                </HeaderCell>
                <HeaderCell
                  metricKey="disapproved_categorized_comparable_not_combined_offers"
                  onMetricClick={handleHeaderClick}
                >
                  Not Combined
                </HeaderCell>
                <HeaderCell />
                <HeaderCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <MerchantRows key={record.id} record={record} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MetricSortContext.Provider>
    </MetricHoverContext.Provider>
  );
}
